import * as Sentry from '@sentry/react-native';
import React, { Component, type ReactNode } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  context?: Record<string, unknown>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { context, onError } = this.props;

    // Enhanced Sentry logging with detailed context
    Sentry?.withScope((scope) => {
      // Set error fingerprint to group similar infinite loop errors
      if (
        error.message.includes('Maximum update depth exceeded') ||
        error.message.includes('Minified React error #185')
      ) {
        scope.setFingerprint(['react-infinite-loop']);
      }

      // Add component stack trace
      scope.setContext('errorBoundary', {
        componentStack: errorInfo.componentStack,
        errorStack: error.stack,
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });

      // Add custom context if provided
      if (context) {
        scope.setContext('customContext', context);
      }

      // Add React error info
      scope.setContext('reactErrorInfo', {
        componentStack: errorInfo.componentStack,
      });

      // Tag the error for easier filtering
      scope.setTag('errorBoundary', true);
      scope.setTag('errorType', 'react-error');

      // Set level based on error type
      if (
        error.message.includes('Maximum update depth') ||
        error.message.includes('185')
      ) {
        scope.setLevel('error');
        scope.setTag('infiniteLoop', true);
      }

      // Capture the error
      Sentry?.captureException(error);
    });

    // Call custom onError handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    const { hasError } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20,
            backgroundColor: '#fff',
          }}
        >
          <View
            style={{
              alignItems: 'center',
              maxWidth: 300,
            }}
          >
            <View
              style={{
                width: 80,
                height: 80,
                borderRadius: 40,
                backgroundColor: '#f5f5f5',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: 24,
              }}
            >
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: '#e5e5e5',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Text
                  style={{
                    fontSize: 20,
                    fontWeight: 'bold',
                    color: '#666',
                  }}
                >
                  !
                </Text>
              </View>
            </View>

            <View style={{ marginBottom: 12 }}>
              <Text
                style={{
                  fontSize: 20,
                  fontWeight: '600',
                  textAlign: 'center',
                  color: '#333',
                  marginBottom: 8,
                }}
              >
                Something went wrong
              </Text>
            </View>

            <Text
              style={{
                fontSize: 16,
                textAlign: 'center',
                color: '#666',
                lineHeight: 22,
                marginBottom: 24,
              }}
            >
              We&apos;re sorry for the inconvenience. The error has been
              reported and our team will look into it.
            </Text>

            <TouchableOpacity
              style={{
                backgroundColor: '#6366f1',
                paddingHorizontal: 24,
                paddingVertical: 12,
                borderRadius: 8,
                marginTop: 8,
              }}
              onPress={() => {
                // Force a refresh by reloading the app
                if (typeof window !== 'undefined' && window.location) {
                  window.location.reload();
                }
              }}
            >
              <Text
                style={{
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: '500',
                  textAlign: 'center',
                }}
              >
                Try Again
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    return children;
  }
}
